import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import {
  CreateTaskDialog, CreateSubtaskDialog, EditProjectDialog, EditTaskDialog,
  TaskDetailDialog, BulkEditTasksDialog, DeleteProjectDialog
} from '../components/projects/ProjectDialogs';
import { ActiveTimerBanner } from '../components/projects/ActiveTimerBanner';
import { ProjectHeader } from '../components/projects/ProjectHeader';
import { ProjectProgressCard, ProjectDetailsCard } from '../components/projects/ProjectStatsCards';
import { ProjectTaskList } from '../components/projects/ProjectTaskList';
import { toast } from 'sonner';

const taskStatuses = [
  { id: 'not_started', label: 'Not Started', color: 'bg-muted text-muted-foreground' },
  { id: 'assigned', label: 'Assigned', color: 'bg-cyan-500/20 text-cyan-600' },
  { id: 'in_progress', label: 'In Progress', color: 'bg-blue-500/20 text-blue-600' },
  { id: 'on_hold', label: 'On Hold', color: 'bg-yellow-500/20 text-yellow-600' },
  { id: 'under_review', label: 'Under Review', color: 'bg-purple-500/20 text-purple-600' },
  { id: 'overdue', label: 'Overdue', color: 'bg-red-500/20 text-red-600' },
  { id: 'completed', label: 'Completed', color: 'bg-green-500/20 text-green-600' }
];

const selectableStatuses = taskStatuses.filter((s) => s.id !== 'overdue');

const priorities = [
  { id: 'low', label: 'Low', color: 'bg-slate-500/20 text-slate-600' },
  { id: 'medium', label: 'Medium', color: 'bg-blue-500/20 text-blue-600' },
  { id: 'high', label: 'High', color: 'bg-orange-500/20 text-orange-600' },
  { id: 'urgent', label: 'Urgent', color: 'bg-red-500/20 text-red-600' }
];

const formatTime = (minutes) => {
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
};

const EMPTY_TASK = { title: '', description: '', priority: 'medium', assigned_to: '', start_date: '', end_date: '', reviewer_id: '', parent_task_id: '' };
const EMPTY_SUBTASK = { title: '', description: '', priority: 'medium', assigned_to: '', reviewer_id: '', start_date: '', end_date: '' };

export const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isTaskDialog, setIsTaskDialog] = useState(false);
  const [isTaskDetailDialog, setIsTaskDetailDialog] = useState(false);
  const [isSubtaskDialog, setIsSubtaskDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [expandedTasks, setExpandedTasks] = useState({});
  const [activeTimer, setActiveTimer] = useState(null);
  const [timerElapsed, setTimerElapsed] = useState(0);
  const [taskForm, setTaskForm] = useState(EMPTY_TASK);
  const [subtaskForm, setSubtaskForm] = useState(EMPTY_SUBTASK);
  const [isEditProjectDialog, setIsEditProjectDialog] = useState(false);
  const [isEditTaskDialog, setIsEditTaskDialog] = useState(false);
  const [projectForm, setProjectForm] = useState({});
  const [editTaskForm, setEditTaskForm] = useState({});
  const [linkedProposal, setLinkedProposal] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [allProposals, setAllProposals] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [isBulkEditDialog, setIsBulkEditDialog] = useState(false);
  const [bulkEditForm, setBulkEditForm] = useState({ assigned_to: '', reviewer_id: '' });

  const isProjectLead = project?.project_lead === user?.user_id;
  const canSeeBudget = ['admin', 'manager'].includes(user?.role) || (user?.role === 'team_lead' && isProjectLead);

  const fetchData = useCallback(async () => {
    try {
      const [projectRes, tasksRes, teamRes, statsRes, timerRes, companiesRes, proposalsRes, clientsRes] = await Promise.all([
        axios.get(`${API_URL}/projects/${projectId}`, { withCredentials: true }),
        axios.get(`${API_URL}/tasks?project_id=${projectId}`, { withCredentials: true }),
        axios.get(`${API_URL}/team`, { withCredentials: true }),
        axios.get(`${API_URL}/projects/${projectId}/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/timer/active`, { withCredentials: true }),
        axios.get(`${API_URL}/companies`, { withCredentials: true }),
        axios.get(`${API_URL}/proposals`, { withCredentials: true }),
        axios.get(`${API_URL}/clients`, { withCredentials: true })
      ]);
      setProject(projectRes.data);
      setTasks(tasksRes.data);
      setTeamMembers(teamRes.data);
      setStats(statsRes.data);
      setCompanies(companiesRes.data);
      setAllClients(clientsRes.data);
      setAllProposals(proposalsRes.data.filter((p) => ['approved', 'sent_to_client', 'confirmed', 'signed', 'converted'].includes(p.status)));
      if (timerRes.data.active) {
        setActiveTimer(timerRes.data);
        setTimerElapsed(timerRes.data.elapsed_minutes);
      }
      if (projectRes.data.proposal_id) {
        try {
          const proposalRes = await axios.get(`${API_URL}/proposals/${projectRes.data.proposal_id}`, { withCredentials: true });
          setLinkedProposal(proposalRes.data);
        } catch { setLinkedProposal(null); }
      }
    } catch {
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!activeTimer) return;
    const interval = setInterval(() => {
      const start = new Date(activeTimer.start_time);
      const now = new Date();
      setTimerElapsed(Math.floor((now - start) / 60000));
    }, 1000);
    return () => clearInterval(interval);
  }, [activeTimer]);

  const handleStartTimer = async (taskId) => {
    try {
      const res = await axios.post(`${API_URL}/timer/start`, null, { params: { task_id: taskId }, withCredentials: true });
      setActiveTimer({ active: true, task_id: taskId, task_title: res.data.task_title, start_time: res.data.start_time });
      setTimerElapsed(0);
      toast.success('Timer started');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start timer');
    }
  };

  const handleStopTimer = async () => {
    try {
      const res = await axios.post(`${API_URL}/timer/stop`, null, { params: { billable: true }, withCredentials: true });
      toast.success(`Timer stopped - ${formatTime(res.data.duration_minutes)} logged`);
      setActiveTimer(null);
      setTimerElapsed(0);
      fetchData();
    } catch {
      toast.error('Failed to stop timer');
    }
  };

  const handleCancelTimer = async () => {
    try {
      await axios.delete(`${API_URL}/timer/cancel`, { withCredentials: true });
      setActiveTimer(null);
      setTimerElapsed(0);
      toast.success('Timer cancelled');
    } catch {
      toast.error('Failed to cancel timer');
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/tasks`, { project_id: projectId, ...taskForm }, { withCredentials: true });
      toast.success('Task created');
      setIsTaskDialog(false);
      setTaskForm(EMPTY_TASK);
      fetchData();
    } catch {
      toast.error('Failed to create task');
    }
  };

  const handleCreateSubtask = async (e) => {
    e.preventDefault();
    if (!selectedTask) return;
    try {
      const res = await axios.post(`${API_URL}/tasks`, {
        project_id: projectId, parent_task_id: selectedTask.task_id, ...subtaskForm
      }, { withCredentials: true });
      await axios.patch(`${API_URL}/tasks/${selectedTask.task_id}`, {
        subtasks: [...(selectedTask.subtasks || []), res.data.task_id]
      }, { withCredentials: true });
      toast.success('Subtask created');
      setIsSubtaskDialog(false);
      setSubtaskForm(EMPTY_SUBTASK);
      fetchData();
      const taskRes = await axios.get(`${API_URL}/tasks/${selectedTask.task_id}`, { withCredentials: true });
      setSelectedTask(taskRes.data);
    } catch {
      toast.error('Failed to create subtask');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/tasks/${taskId}`, { status: newStatus }, { withCredentials: true });
      toast.success('Status updated');
      fetchData();
    } catch {
      toast.error('Failed to update task');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await axios.post(`${API_URL}/tasks/${selectedTask.task_id}/add-comment`, null,
        { params: { comment: newComment }, withCredentials: true });
      toast.success('Comment added');
      setNewComment('');
      const taskRes = await axios.get(`${API_URL}/tasks/${selectedTask.task_id}`, { withCredentials: true });
      setSelectedTask(taskRes.data);
      fetchData();
    } catch {
      toast.error('Failed to add comment');
    }
  };

  const parentTasks = useMemo(() => {
    return [...tasks].filter((t) => !t.parent_task_id).sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  }, [tasks]);

  const subtasksByParent = useMemo(() => {
    const map = {};
    tasks.forEach((t) => {
      if (t.parent_task_id) {
        if (!map[t.parent_task_id]) map[t.parent_task_id] = [];
        map[t.parent_task_id].push(t);
      }
    });
    Object.keys(map).forEach((key) => {
      map[key].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    });
    return map;
  }, [tasks]);

  const getStatusBadge = (status) => {
    const info = taskStatuses.find((s) => s.id === status) || taskStatuses[0];
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${info.color}`}>{info.label}</span>;
  };

  const getPriorityBadge = (priority) => {
    const info = priorities.find((p) => p.id === priority) || priorities[1];
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${info.color}`}>{info.label}</span>;
  };

  const openTaskDetail = (task) => {
    setSelectedTask(task);
    setIsTaskDetailDialog(true);
  };

  const handleDeleteProject = async () => {
    try {
      await axios.delete(`${API_URL}/projects/${projectId}`, { withCredentials: true });
      toast.success('Project deleted');
      navigate('/projects');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
      setShowDeleteConfirm(false);
    }
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API_URL}/projects/${projectId}`, projectForm, { withCredentials: true });
      toast.success('Project updated');
      setIsEditProjectDialog(false);
      fetchData();
    } catch {
      toast.error('Failed to update project');
    }
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API_URL}/tasks/${selectedTask.task_id}`, editTaskForm, { withCredentials: true });
      toast.success('Task updated');
      setIsEditTaskDialog(false);
      fetchData();
      const res = await axios.get(`${API_URL}/tasks/${selectedTask.task_id}`, { withCredentials: true });
      setSelectedTask(res.data);
    } catch {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Delete this task and all its subtasks? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/tasks/${taskId}`, { withCredentials: true });
      toast.success('Task deleted');
      setIsTaskDetailDialog(false);
      fetchData();
    } catch {
      toast.error('Failed to delete task');
    }
  };

  const handleBulkDeleteTasks = async () => {
    if (!window.confirm(`Delete ${selectedTaskIds.length} selected task(s)? This cannot be undone.`)) return;
    try {
      await Promise.all(selectedTaskIds.map((id) => axios.delete(`${API_URL}/tasks/${id}`, { withCredentials: true })));
      toast.success(`${selectedTaskIds.length} task(s) deleted`);
      setSelectedTaskIds([]);
      fetchData();
    } catch {
      toast.error('Failed to delete some tasks');
    }
  };

  const handleBulkEditTasks = async () => {
    const updates = {};
    if (bulkEditForm.assigned_to) updates.assigned_to = bulkEditForm.assigned_to;
    if (bulkEditForm.reviewer_id) updates.reviewer_id = bulkEditForm.reviewer_id;
    if (Object.keys(updates).length === 0) {
      toast.error('Please select at least one field to update');
      return;
    }
    try {
      await Promise.all(selectedTaskIds.map((id) => axios.patch(`${API_URL}/tasks/${id}`, updates, { withCredentials: true })));
      toast.success(`${selectedTaskIds.length} task(s) updated`);
      setSelectedTaskIds([]);
      setIsBulkEditDialog(false);
      setBulkEditForm({ assigned_to: '', reviewer_id: '' });
      fetchData();
    } catch {
      toast.error('Failed to update some tasks');
    }
  };

  const toggleTaskSelection = (taskId) => {
    setSelectedTaskIds((prev) => prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]);
  };

  const toggleAllTasks = () => {
    if (selectedTaskIds.length === parentTasks.length) {
      setSelectedTaskIds([]);
    } else {
      setSelectedTaskIds(parentTasks.map((t) => t.task_id));
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  if (!project) {
    return <Card className="p-12 text-center"><p className="text-muted-foreground">Project not found</p></Card>;
  }

  const isAdmin = user?.role === 'admin';

  return (
    <div data-testid="project-detail-page" className="pt-12">
      <ProjectHeader
        project={project}
        teamMembers={teamMembers}
        navigate={navigate}
        onEdit={() => { setProjectForm(project); setIsEditProjectDialog(true); }}
        onAddTask={() => setIsTaskDialog(true)}
        onDeleteRequest={() => setShowDeleteConfirm(true)}
      />

      <ActiveTimerBanner
        activeTimer={activeTimer}
        timerElapsed={timerElapsed}
        onStop={handleStopTimer}
        onCancel={handleCancelTimer}
        formatTime={formatTime}
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
        <ProjectProgressCard stats={stats} />
        <ProjectDetailsCard
          project={project}
          linkedProposal={linkedProposal}
          stats={stats}
          canSeeBudget={canSeeBudget}
          navigate={navigate}
        />
      </div>

      <ProjectTaskList
        parentTasks={parentTasks}
        subtasksByParent={subtasksByParent}
        expandedTasks={expandedTasks}
        setExpandedTasks={setExpandedTasks}
        isAdmin={isAdmin}
        selectedTaskIds={selectedTaskIds}
        onToggleSelect={toggleTaskSelection}
        onToggleAll={toggleAllTasks}
        onBulkEdit={() => setIsBulkEditDialog(true)}
        onBulkDelete={handleBulkDeleteTasks}
        onClearSelection={() => setSelectedTaskIds([])}
        activeTimer={activeTimer}
        onStart={handleStartTimer}
        onStatusChange={handleStatusChange}
        openDetail={openTaskDetail}
        getStatusBadge={getStatusBadge}
        getPriorityBadge={getPriorityBadge}
        teamMembers={teamMembers}
        formatTime={formatTime}
        statuses={selectableStatuses}
      />

      <CreateTaskDialog
        open={isTaskDialog} onOpenChange={setIsTaskDialog}
        form={taskForm} setForm={setTaskForm}
        onSubmit={handleCreateTask} parentTasks={parentTasks} teamMembers={teamMembers}
      />
      <CreateSubtaskDialog
        open={isSubtaskDialog} onOpenChange={setIsSubtaskDialog}
        form={subtaskForm} setForm={setSubtaskForm}
        onSubmit={handleCreateSubtask} parentTaskTitle={selectedTask?.title} teamMembers={teamMembers}
      />
      <TaskDetailDialog
        open={isTaskDetailDialog} onOpenChange={setIsTaskDetailDialog}
        task={selectedTask} teamMembers={teamMembers}
        subtasks={selectedTask ? (subtasksByParent[selectedTask.task_id] || []) : []}
        comments={selectedTask?.comments || []} commentText={newComment} setCommentText={setNewComment}
        onAddComment={handleAddComment} onStatusChange={handleStatusChange}
        onEditTask={(task) => { setEditTaskForm(task); setIsEditTaskDialog(true); }}
        onDeleteTask={handleDeleteTask}
        onCreateSubtask={(task) => { setSelectedTask(task); setSubtaskForm(EMPTY_SUBTASK); setIsSubtaskDialog(true); }}
        getStatusBadge={getStatusBadge} getPriorityBadge={getPriorityBadge} formatTime={formatTime}
      />
      <EditProjectDialog
        open={isEditProjectDialog} onOpenChange={setIsEditProjectDialog}
        form={projectForm} setForm={setProjectForm}
        onSubmit={handleUpdateProject} proposals={allProposals} companies={companies}
        teamMembers={teamMembers} canSeeBudget={canSeeBudget} clients={allClients}
      />
      <EditTaskDialog
        open={isEditTaskDialog} onOpenChange={setIsEditTaskDialog}
        form={editTaskForm} setForm={setEditTaskForm}
        onSubmit={handleUpdateTask} teamMembers={teamMembers}
      />
      <DeleteProjectDialog
        open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}
        onConfirm={handleDeleteProject}
      />
      <BulkEditTasksDialog
        open={isBulkEditDialog} onOpenChange={setIsBulkEditDialog}
        count={selectedTaskIds.length} form={bulkEditForm} setForm={setBulkEditForm}
        onSubmit={handleBulkEditTasks} teamMembers={teamMembers}
      />
    </div>
  );
};
